import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReplaySubject, takeUntil } from 'rxjs';

import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from 'app/modules/social/modal-simple-social/modal-simple-social.component';
import { RoomSizeInfo } from 'app/modules/social/types/room';
import SocialTheme from 'entity/SocialTheme';
import SocialThemeType from 'enums/SocialThemeType';

import { RoomSizes } from './constants';

const MIN_CAPACITY = '10';
const MIN_SIZE = '2000';

@Component({
  selector: 'app-room-size-card',
  templateUrl: './room-size-card.component.html',
  styleUrls: ['./room-size-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomSizeComponent implements OnDestroy, OnChanges {
  private destroyed$ = new ReplaySubject<boolean>(1);

  @Input() public hasBackStepButton = true;
  @Input() public isCreateFlow = true;
  @Input() public roomSize?: RoomSizeInfo;
  @Input() public theme?: SocialTheme;
  @Output() public roomSizeChange = new EventEmitter<RoomSizeInfo>();
  @Output() public backStepClick = new EventEmitter<boolean>();
  @Output() public changeStep = new EventEmitter<number>();

  protected roomSizesExamples = RoomSizes;
  protected maxRoomCapacity = MIN_CAPACITY;
  protected selectedSize = MIN_SIZE;
  protected roomCapacity = MIN_CAPACITY;
  protected label = '';

  public constructor(private dialog: MatDialog) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.roomSize && changes.roomSize.currentValue) {
      this.roomCapacity = changes.roomSize.currentValue.capacity;
      this.selectedSize = changes.roomSize.currentValue.size;
      this.maxRoomCapacity = changes.roomSize.currentValue.capacity;
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public backStep(): void {
    this.backStepClick.emit(true);
  }

  public nextStep(): void {
    if (
      !this.theme?.multiSize &&
      this.roomSize?.size &&
      this.selectedSize > this.roomSize.size &&
      this.theme?.type === SocialThemeType.SINGLE_IMAGE
    ) {
      const _data = {
        modalTitle: 'New Background Image Needed',
        text: `The background image you previously selected is too small for this room size. To continue with this size room, please select a new background.`,
        primaryButtonText: 'Proceed',
        customButtonText: 'Cancel',
      };
      this.displayModal(_data);
      return;
    }
    this.roomSizeChange.emit({ capacity: this.roomCapacity, size: this.selectedSize, label: this.label });
  }

  public getImagePath(imageName: string): string {
    return `/assets/images/room-sizes/map-size-${imageName}.svg`;
  }

  public setRoomMaxCapacity(maxParticipants: string, size: string, title: string): void {
    this.maxRoomCapacity = maxParticipants;
    this.selectedSize = size;
    this.roomCapacity = maxParticipants;
    this.label = title;
  }

  private displayModal(data: ModalSimpleSocialData) {
    const ref = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data,
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        if (res === 'primary') {
          this.changeStep.emit(3);
        }

        if (res === 'custom') {
          this.selectedSize = this.roomSize?.size ?? '';
        }
      });
  }
}
